import EngineCleanupBase from '../../../../services/engines/EngineCleanupBase';
import SearchResultProvider from '../FloatingContentPanel/SearchDrawer/Core/SearchResultProvider';
import noteOptionsPanelEngine from '../../NoteOptionsPanel/Core/NoteOptionsPanelEngine';
import floatingPanelEngine_OnEntryNodesResolved from './OnEntryNodesResolved';
import topicGroupCache from '../../YourNotesTab/YourNotesGroupManager/TopicGroupCache';

import jxWindowManager from '../../../../JxControls/JxWindowManager/JxWindowManager.jsx';
import commandEngine from '../CommandBarWidget/Commands/CommandEngine';

const _name = 'FloatingPanelEngine';

// Tab id as defined in tabDefinitions.js — must match exactly for JxWindowManager owner filter
const LIBRARY_TAB_ID = 'library';

/**
 * Manages FloatingContentPanel domain logic — provider lifecycle, topics, commands.
 * Window instance lifecycle (open/close/render) is delegated to JxWindowManager.
 * _providers map: windowId → SearchResultProvider
 */
class FloatingPanelEngine extends EngineCleanupBase {
    _providers = {}; // windowId → SearchResultProvider
    _topicOptions = [];

    bootup = () => {
        this.initialize(_name);

        // register commands
        commandEngine.bootup();
    };

    /**
     * Update journal when SearchResultProvider contains the journal with the same rk.
     * @param {object} cmd of type OnEntryNodesResolved
     */
    OnEntryNodesResolved = (cmd) => {
        floatingPanelEngine_OnEntryNodesResolved.handle(cmd, this.getProvider);
    };

    /**
     * Load topics from cache.
     */
    getTopics = () => {
        const topics = topicGroupCache.getActiveGroups();
        if (topics.length === 0) return;

        this._topicOptions = [{ label: 'All topics', value: 'All topics' }, ...topics.map((t) => ({ label: t.name, value: t.name }))];
    };

    /**
     * Opens a new FloatingContentPanel via JxWindowManager.
     * Creates a SearchResultProvider for the assigned window id.
     * @param {string} title
     * @param {string} initialQuery
     */
    openPanel = (title, initialQuery = '') => {
        title = `Query: ${title}`;
        const id = jxWindowManager.openWindow('FloatingContentPanel', LIBRARY_TAB_ID, { title, initialQuery });
        if (!id) return;

        const provider = new SearchResultProvider(id);
        this._providers[id] = provider;

        if (initialQuery.trim().length >= 3) {
            provider.provide(initialQuery);
        }
    };

    /**
     * Closes a FloatingContentPanel — cleans up provider, delegates window removal to JxWindowManager.
     * @param {string} id
     */
    closePanel = async (id) => {
        const provider = this._providers[id];
        if (provider) {
            await provider.cleanup(id);
            delete this._providers[id];
        }
        jxWindowManager.closeWindow(id);
    };

    /**
     * Exits focus mode on all open panels.
     */
    clearAllFocusMode = () => {
        Object.keys(this._providers).forEach((id) => {
            noteOptionsPanelEngine.exitFocus(id);
        });
    };

    /**
     * Returns the SearchResultProvider for a panel.
     * @param {string} panelId
     * @returns {SearchResultProvider|null}
     */
    getProvider = (panelId) => this._providers[panelId] ?? null;

    /** @returns {{ label, value }[]} shared topic options for all panels */
    getTopicOptions = () => this._topicOptions;

    clear() {
        super.clear();
        this._providers = {};
    }
}

const floatingPanelEngine = new FloatingPanelEngine();
export default floatingPanelEngine;
